import React from 'react';
import { ResponsiveLine } from '@nivo/line';
import { formatDate, isWithinRange } from 'shared/src/date-string-utils/date-utils';
import { ISODateString } from 'shared/src/date-string-utils/types';
import { formatMoney } from 'shared/src/utils/money/money-utils';
import { PacingBlockV2 } from 'shared/src/api/line-item-types';
import { Day, getUncoveredDateRangesV2 } from '@/features/line-items/pacing-helpers';

type PacingChartV2Props = {
  data: Day[];
  blocks: PacingBlockV2[];
  /** Whether to show cumulative or daily view. Defaults to true (cumulative). */
  isCumulative?: boolean;
};

export function PacingChartV2({ data, blocks, isCumulative = true }: PacingChartV2Props) {
  const dataWithDaily = data.map((day, idx) => ({
    ...day,
    dailyValue:
      idx === 0 ? day.cumulativeTotal : day.cumulativeTotal - data[idx - 1].cumulativeTotal
  }));

  const getValue = (day: (typeof dataWithDaily)[0]) =>
    isCumulative ? day.cumulativeTotal : day.dailyValue;
  const maxY = Math.max(...dataWithDaily.map(getValue));

  // Block colors matching the block components
  const BLOCK_COLORS = ['#a855f7', '#14b8a6', '#3b82f6', '#ec4899']; // purple, teal, blue, pink
  const UNCOVERED_COLOR = '#e5e7eb'; // gray-200

  const startDate = dataWithDaily[0]?.day;
  const endDate = dataWithDaily[dataWithDaily.length - 1]?.day;

  let chartData = [];

  if (blocks.length > 0 && startDate && endDate) {
    const uncoveredRanges = getUncoveredDateRangesV2(blocks, startDate, endDate);

    const coveredSeries = blocks.map((block, idx) => {
      const blockData = dataWithDaily
        .filter(day => isWithinRange(day.day, block.dates))
        .map(day => ({ x: day.day, y: getValue(day) }));

      return {
        id: `Block ${idx + 1}`,
        data: blockData,
        color: BLOCK_COLORS[idx % BLOCK_COLORS.length]
      };
    });

    const uncoveredSeries = uncoveredRanges.map((range, idx) => {
      const rangeData = dataWithDaily
        .filter(day => isWithinRange(day.day, range))
        .map(day => ({ x: day.day, y: getValue(day) }));

      return {
        id: `Uncovered ${idx + 1}`,
        data: rangeData,
        color: UNCOVERED_COLOR
      };
    });

    chartData = [...coveredSeries, ...uncoveredSeries];
  } else {
    const fullLineData = dataWithDaily.map(day => ({
      x: day.day,
      y: getValue(day)
    }));
    chartData = [{ id: 'Full Range', data: fullLineData, color: UNCOVERED_COLOR }];
  }

  return (
    <div className="h-[250px] w-full min-w-0">
      <ResponsiveLine
        margin={{ top: 20, right: 20, bottom: 60, left: 90 }}
        animate
        data={chartData}
        colors={d => d.color || BLOCK_COLORS[0]}
        enableSlices="x"
        sliceTooltip={({ slice }) => (
          <div className="mt-10 rounded-sm bg-white p-4 shadow-xl">
            <div className="flex justify-between">
              <div className="text-sm font-light text-gray-400">Date:</div>
              <div>{formatDate(ISODateString.parse(slice.points[0].data.xFormatted))}</div>
            </div>
            <div className="flex justify-between ">
              <div className="mr-6 text-sm font-light text-gray-400">
                {isCumulative ? 'Cumulative Total:' : 'Daily Spend:'}
              </div>
              <div>{formatMoney(slice.points[0].data.y as number)}</div>
            </div>
          </div>
        )}
        xScale={{
          type: 'time',
          format: '%Y-%m-%d',
          useUTC: false,
          precision: 'day'
        }}
        xFormat="time:%Y-%m-%d"
        yScale={{
          type: 'linear',
          max: maxY * 1.25
        }}
        enableArea
        areaOpacity={0.7}
        axisLeft={{
          legend: isCumulative ? 'Cumulative Spend' : 'Daily Spend',
          legendOffset: -60,
          legendPosition: 'middle',
          format: (value: number) => `$${value}`
        }}
        axisBottom={{
          format: '%b %d',
          tickValues: calcTickValues(data.length)
        }}
        enablePoints={false}
        useMesh={true}
        defs={[
          {
            id: 'purpleGradient',
            type: 'linearGradient',
            colors: [
              { offset: 0, color: 'rgba(168, 85, 247, 0.8)' },
              { offset: 100, color: 'rgba(168, 85, 247, 0.3)' }
            ]
          },
          {
            id: 'tealGradient',
            type: 'linearGradient',
            colors: [
              { offset: 0, color: 'rgba(20, 184, 166, 0.8)' },
              { offset: 100, color: 'rgba(20, 184, 166, 0.3)' }
            ]
          },
          {
            id: 'blueGradient',
            type: 'linearGradient',
            colors: [
              { offset: 0, color: 'rgba(59, 130, 246, 0.8)' },
              { offset: 100, color: 'rgba(59, 130, 246, 0.3)' }
            ]
          },
          {
            id: 'pinkGradient',
            type: 'linearGradient',
            colors: [
              { offset: 0, color: 'rgba(236, 72, 153, 0.8)' },
              { offset: 100, color: 'rgba(236, 72, 153, 0.3)' }
            ]
          },
          {
            id: 'grayGradient',
            type: 'linearGradient',
            colors: [
              { offset: 0, color: 'rgba(229, 231, 235, 0.8)' },
              { offset: 100, color: 'rgba(229, 231, 235, 0.3)' }
            ]
          }
        ]}
        fill={[
          { match: { id: 'Block 1' }, id: 'purpleGradient' },
          { match: { id: 'Block 2' }, id: 'tealGradient' },
          { match: { id: 'Block 3' }, id: 'blueGradient' },
          { match: { id: 'Block 4' }, id: 'pinkGradient' },
          { match: d => d.id.startsWith('Uncovered'), id: 'grayGradient' },
          { match: { id: 'Full Range' }, id: 'grayGradient' }
        ]}
      />
    </div>
  );
}

function calcTickValues(numDays: number) {
  if (numDays < 360) return 'every 1 months';
  else if (numDays < 720) return 'every 2 months';
  else if (numDays < 1800) return 'every 3 months';
  else return 'every 6 months';
}
